'use client'

import * as React from 'react'
import Link from 'next/link'
import { X } from 'lucide-react'
import { canShow, markShown, markConverted } from '@/lib/growth/frequency'
import { track, trackBookDemo } from '@/lib/analytics/ga4'
import { GoogleContinueButton } from './google-continue-button'
import { useFocusTrap } from '@/lib/use-focus-trap'

// Exit-intent demo prompt for high-intent pages (compare, alternatives).
// Desktop: fires when the cursor leaves through the top of the viewport.
// Mobile: fires once at 60% scroll depth after 30s of dwell, which is the
// closest honest analogue to exit intent on touch.
// Frequency: one impression per visitor per 7 days across ALL surfaces
// (shared cap key), 90 days after a click-through. See lib/growth/frequency.

const CAP_KEY = 'exit-intent-demo'

export function ExitIntentDemo({ surface, competitorName }: { surface: string; competitorName?: string }) {
  const [open, setOpen] = React.useState(false)
  const armed = React.useRef(false)
  const dwellStart = React.useRef(0)
  const closeRef = React.useRef<HTMLButtonElement>(null)
  const dialogRef = React.useRef<HTMLDivElement>(null)

  useFocusTrap(dialogRef, open)

  const fire = React.useCallback(() => {
    if (armed.current || !canShow(CAP_KEY)) return
    armed.current = true
    markShown(CAP_KEY)
    setOpen(true)
    track('growth_exit_intent_shown', { surface })
  }, [surface])

  React.useEffect(() => {
    dwellStart.current = Date.now()

    function onMouseOut(e: MouseEvent) {
      if (e.relatedTarget === null && e.clientY <= 0) fire()
    }
    function onScroll() {
      const isTouch = window.matchMedia('(pointer: coarse)').matches
      if (!isTouch) return
      const depth = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight
      if (depth > 0.6 && Date.now() - dwellStart.current > 30_000) fire()
    }

    document.addEventListener('mouseout', onMouseOut)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      document.removeEventListener('mouseout', onMouseOut)
      window.removeEventListener('scroll', onScroll)
    }
  }, [fire])

  React.useEffect(() => {
    if (!open) return
    closeRef.current?.focus()
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') dismiss('escape')
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function dismiss(how: string) {
    setOpen(false)
    track('growth_exit_intent_dismissed', { surface, how })
  }

  function onBook() {
    markConverted(CAP_KEY)
    trackBookDemo(surface)
    setOpen(false)
  }

  if (!open) return null

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-demo-title"
      className="fixed inset-0 z-[70] flex items-end justify-center p-4 sm:items-center"
    >
      <button
        aria-label="Close"
        tabIndex={-1}
        onClick={() => dismiss('backdrop')}
        className="absolute inset-0 bg-brand-navy/50 backdrop-blur-[2px] motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
      />
      <div className="relative w-full max-w-md rounded-2xl border bg-card p-7 shadow-xl motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 motion-safe:duration-300">
        <button
          ref={closeRef}
          aria-label="Close and keep reading"
          onClick={() => dismiss('x')}
          className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <X className="h-4 w-4" />
        </button>

        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">Live demo, 15 minutes</p>
        <h2 id="exit-demo-title" className="mt-3 font-display text-2xl font-extrabold leading-snug tracking-tight text-balance">
          {competitorName ? `Tables only get you so far.` : `Reading only gets you so far.`}
        </h2>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground text-pretty">
          Book 15 minutes and we&rsquo;ll load your own products and social content into a live
          shoppable gallery{competitorName ? ` while you still have the ${competitorName} tab open` : ''}. You
          watch, you decide. A real human, no slide deck.
        </p>

        <div className="mt-6 flex flex-col gap-2">
          <Link
            href="/demo"
            onClick={onBook}
            className="inline-flex h-11 items-center justify-center rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground transition hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            Show me on my store
          </Link>

          <div className="my-1 flex items-center gap-3" role="separator" aria-label="or">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <GoogleContinueButton
            surface={`exit-intent-${surface}`}
            onDone={(result) => {
              if (result.ok) markConverted(CAP_KEY)
            }}
          />

          <button
            onClick={() => dismiss('keep-reading')}
            className="h-9 rounded-full text-sm text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            I&rsquo;ll keep reading
          </button>
        </div>
      </div>
    </div>
  )
}
